import { BoundingBox } from '../configurator/model/bounding-box';
import { View } from '../configurator/model/view';
import { Coordinate } from '../configurator/model/coordinate';
import { EditMode } from '../configurator/model/edit-mode';
import HeightsAreaRenderer from './model/heights-area-renderer';
import { ActiveToolMode } from './model/active-tool-mode';
import { getSelectionFlattenPolygon, selectAreaPointsByPolygon } from './model/group-selection';
import { switchCursor } from '../configurator/helpers';

export class HeightsRenderer {
  view = new View();
  planareas = [];
  eventTokens = {};
  documentEventTokens = {};
  maxPlanareaId = 0;

  constructor(options) {
    ({
      canvasSelector: this.canvasSelector = '',
      isFullScreen: this.isFullScreen = false,
      editState: this.editState,
      onAfterInit: this.onAfterInit,
      additionalOptions: this.additionalOptions = {},
      editMode: this.editMode,
      translator: this.translator,
      onPointAdded: this.onPointAdded,
      onPointClicked: this.onPointClicked,
      onSlopeStartSelected: this.onSlopeStartSelected,
      onAllSlopesDefined: this.onAllSlopesDefined,
      onGroupSelection: this.onGroupSelection,
      highlightColor: this.highlightColor,
      onPointRemoved: this.onPointRemoved,
      onSlopeCalculation: this.onSlopeCalculation,
    } = options);

    this.canvasElement = document.querySelector(this.canvasSelector);
    this.view.zoomMultiplier = options.zoomMultiplier || 1.2;
    this.view.canvasElement = this.canvasElement;
    this.selectColor = '#0015a2f0';
    this.selectStyle = 'Dash';
    this.selectWidth = '1';
    this.init();
  }

  init() {
    dojo.require('dojo.html');
    dojo.require('dojox.gfx');
    dojo.require('dojox.gfx.Moveable');
    dojo.addOnLoad(() => {
      this.prepareCanvas();
      this.initAreas();
      this.fitPlanToView();
      this.redraw();
      if (this.onAfterInit) this.onAfterInit();
    });
  }

  prepareCanvas() {
    this.surface = dojox.gfx.createSurface(this.canvasElement);
    this.surface.setDimensions(this.canvasElement.offsetWidth, this.canvasElement.offsetHeight);
    this._resetGroups();
  }

  _resetGroups() {
    this.group ? this.group.clear() : (this.group = this.surface.createGroup());
    this.areasGroup = this.group.createGroup();
    this.groupSelectGroup = this.group.createGroup();
  }

  initAreas() {
    this.areaRenderers = [];
    this.boundingBox = null;
    this.editState.areas.forEach(areaState => {
      const boundingRect = new parkside.poly(areaState.points).getBoundingRect();
      const boundingBox = new BoundingBox(
        new Coordinate(boundingRect[1].x, boundingRect[1].y),
        new Coordinate(boundingRect[0].x, boundingRect[0].y)
      );

      this.areaRenderers.push(
        new HeightsAreaRenderer({
          areaState: areaState,
          group: this.areasGroup,
          view: this.view,
          onPointAdded: this.onPointAdded,
          onPointClicked: this.onPointClicked,
          onSlopeStartSelected: this.onSlopeStartSelected,
          onSlopeConfirmed: this.slopeConfirmed.bind(this),
          highlightColor: this.highlightColor,
          onPointRemoved: this.onPointRemoved,
        })
      );
      this.boundingBox ? this.boundingBox.combineWith(boundingBox) : (this.boundingBox = boundingBox);
    });
  }

  slopeToolSelected() {
    this.removeGroupSelect();

    this.editState.areas.forEach(area => {
      area.slopeDefined = false;
      area.slopeStartPoint = null;
    });

    this.areaRenderers.forEach(renderer => {
      renderer.activeToolMode = ActiveToolMode.SLOPE_START_SELECTION;
    });

    this.slopeInteracted();
  }

  slopeInteracted() {
    this.areaRenderers.forEach(renderer => {
      const areaState = renderer.areaState;
      areaState.removePointHighlights();
      if (areaState.slopeStartPoint) {
        areaState.slopeStartPoint.highlighted = true;
      } else if (!renderer.areaState.slopeDefined) {
        for (let point of renderer.areaState.minHeightPoints) {
          point.highlighted = point.isInitial;
        }
      }
      if (renderer.areaState.slopeDefined) {
        renderer.activeToolMode = ActiveToolMode.NONE;
      } else if (renderer.areaState.slopeStartPoint) {
        renderer.activeToolMode = ActiveToolMode.SLOPE_SELECTION;
      } else {
        renderer.activeToolMode = ActiveToolMode.SLOPE_START_SELECTION;
      }
    });
  }

  slopeConfirmed(areaState) {
    areaState.confirmSlope();
    if (this.editState.allSlopesDefined) {
      this.onAllSlopesDefined();
    } else {
      this.slopeInteracted();
      this.onSlopeCalculation();
    }
  }

  addPointsToolSelected() {
    this.editState.clearPointHighlights();
    this.removeGroupSelect();
    this.areaRenderers.forEach(area => {
      area.activeToolMode = ActiveToolMode.ADD_POINT;
    });
  }

  editPointsToolSelected() {
    this.editState.clearPointHighlights();
    this.areaRenderers.forEach(area => {
      area.activeToolMode = ActiveToolMode.ADD_POINT;
      area.render();
    });
    this._applyGroupSelect();
  }

  _applyGroupSelect() {
    let selectionStarted = false;
    let start = null;
    let end = null;

    this.groupSelectGroup ? this.groupSelectGroup.clear() : this.groupSelectGroup.createGroup();
    const containerPosition = dojo.position(this.canvasElement, true);
    const rectGroup = this.groupSelectGroup.createGroup();
    const detectionArea = this.groupSelectGroup.createGroup();
    detectionArea
      .createRect({ x: 0, y: 0, width: containerPosition.w, height: containerPosition.h })
      .setFill('rgba(0,0,0,0)');
    detectionArea.connect('mousedown', e => {
      selectionStarted = true;
      let currentPosition = new Coordinate(e.pageX, e.pageY);
      start = this.view.convertPageToLocal(currentPosition);
    });
    detectionArea.connect('mousemove', e => {
      end = this.view.convertPageToLocal(new Coordinate(e.pageX, e.pageY));
      if (selectionStarted && start) {
        rectGroup.clear();
        rectGroup
          .createRect({
            x: Math.min(start.x, end.x),
            y: Math.min(start.y, end.y),
            width: Math.abs(end.x - start.x),
            height: Math.abs(end.y - start.y),
          })
          .setStroke({ color: this.selectColor, width: this.selectWidth, style: this.selectStyle });
        this._highlightGroupSelection(start, end);
      }
    });
    detectionArea.connect('mouseout', e => {
      end = this.view.convertPageToLocal(new Coordinate(e.pageX, e.pageY));
      if (selectionStarted) {
        rectGroup.clear();
        selectionStarted = false;
        this._makeGroupSelection(start, end);
      }
    });
    detectionArea.connect('mouseup', e => {
      if (selectionStarted) {
        rectGroup.clear();
        selectionStarted = false;
        this._makeGroupSelection(start, end);
      }
    });
  }

  _makeGroupSelection(startPoint, endPoint) {
    const selectionPolygon = getSelectionFlattenPolygon(
      this.view.convertLocalToGlobal(startPoint),
      this.view.convertLocalToGlobal(endPoint)
    );

    const selectedPoints = [];
    this.editState.areas.forEach(area => {
      const points = selectAreaPointsByPolygon(area, selectionPolygon);
      if (points.length) {
        selectedPoints.push(...points);
      }
    });

    if (selectedPoints.length) {
      this.onGroupSelection(selectedPoints, startPoint);
    }
  }

  _highlightGroupSelection(startPoint, endPoint) {
    const selectionPolygon = getSelectionFlattenPolygon(
      this.view.convertLocalToGlobal(startPoint),
      this.view.convertLocalToGlobal(endPoint)
    );

    this.editState.clearPointHighlights();
    this.editState.areas.forEach(area => {
      const points = selectAreaPointsByPolygon(area, selectionPolygon);
      points.forEach(point => (point.highlighted = true));
    });

    this.areaRenderers.forEach(areaRenderer => areaRenderer.renderPoints());
  }

  removeGroupSelect() {
    this.groupSelectGroup.clear();
  }

  minHeightToolSelected() {
    this.areaRenderers.forEach(area => {
      area.activeToolMode = ActiveToolMode.MIN_HEIGHT_SELECTION;
    });
  }

  refresh() {
    this._resetGroups();
    this.initPlanareas();
  }

  redraw(visualOptions) {
    this.areaRenderers.forEach(areaRenderer => {
      areaRenderer.render(visualOptions);
    });
  }

  fitPlanToView() {
    this._updateViewOffset();
    const padding = 100;
    const containerPosition = {
      w: this.canvasElement.offsetWidth,
      h: this.canvasElement.getAttribute('data-max-height'),
    };

    const rzoom = Math.max(
      (this.boundingBox.max.x - this.boundingBox.min.x) / (containerPosition.w - padding),
      (this.boundingBox.max.y - this.boundingBox.min.y) / (containerPosition.h - padding)
    );

    this.view.zoomLevel = -1 * Math.ceil(Math.log(rzoom) / Math.log(this.view.zoomMultiplier));

    const pzoom = Math.pow(this.view.zoomMultiplier, this.view.zoomLevel);
    const newHeight = Math.min(
      (this.boundingBox.max.y - this.boundingBox.min.y) * pzoom + padding + 48,
      containerPosition.h
    );

    this.canvasElement.style.height = newHeight + 'px';

    this.view.pan.x =
      Math.floor(0.5 * (containerPosition.w - (this.boundingBox.max.x - this.boundingBox.min.x) * pzoom)) -
      this.boundingBox.min.x * pzoom;
    this.view.pan.y =
      Math.floor(0.5 * (containerPosition.h - (this.boundingBox.max.y - this.boundingBox.min.y) * pzoom)) -
      this.boundingBox.min.y * pzoom -
      (containerPosition.h - newHeight) * 0.5;
    this.view.zoomPan.x = 0;
    this.view.zoomPan.y = 0;

    this.surface.setDimensions(this.canvasElement.offsetWidth, this.canvasElement.offsetHeight);
  }

  zoomIn(zoomPoint) {
    const surfaceZoomPoint = zoomPoint || this._getCanvasCenter();
    this.view.zoomIn(surfaceZoomPoint);
    this.redraw();
  }

  zoomOut(zoomPoint) {
    const surfaceZoomPoint = zoomPoint || this._getCanvasCenter();
    this.view.zoomOut(surfaceZoomPoint);
    this.redraw();
  }

  _getCanvasCenter() {
    return new Coordinate(
      parseFloat(this.canvasElement.offsetWidth) / 2.0,
      parseFloat(this.canvasElement.offsetHeight) / 2.0
    );
  }

  switchEditMode(mode) {
    this._resetEventTokens();

    switch (mode) {
      case EditMode.POINTER:
        this.moveGroup.clear();
        break;
      case EditMode.MOVE:
        this._applyMoveMode();
        break;
    }
  }

  _applyMoveMode() {
    const containerPosition = dojo.position(this.canvasElement, true);
    this.moveGroup ? this.moveGroup.clear() : (this.moveGroup = this.surface.createGroup());
    this.moveGroup
      .createRect({ x: 0, y: 0, width: containerPosition.w, height: containerPosition.h })
      .setFill('transparent');
    let oldPosition = null;
    let isMoving = false;

    this.eventTokens['editModeMoveStart'] = this.moveGroup.connect('mousedown', e => {
      e.preventDefault();
      e.stopPropagation();

      if (!isMoving) {
        isMoving = true;
        this.eventTokens['editModeMoveMoving'] = this.moveGroup.connect('mousemove', e => {
          let currentPosition = new Coordinate(e.pageX, e.pageY);
          if (!oldPosition) {
            oldPosition = currentPosition;
          } else {
            this.view.move(oldPosition, currentPosition);
            oldPosition = currentPosition;
            this.redraw();
          }
        });
      }
    });

    this.documentEventTokens['editModeMoveEnd'] = e => {
      oldPosition = null;
      if (this.eventTokens['editModeMoveMoving']) {
        this.moveGroup.disconnect(this.eventTokens['editModeMoveMoving']);
        isMoving = false;
      }
    };

    document.addEventListener('mouseup', this.documentEventTokens['editModeMoveEnd']);
  }

  _resetEventTokens() {
    if (this.eventTokens['editModeMoveMoving'] !== undefined) {
      this.surface.disconnect(this.eventTokens['editModeMoveMoving']);
    }
    if (this.eventTokens['editModeMoveStart'] !== undefined) {
      this.surface.disconnect(this.eventTokens['editModeMoveStart']);
    }
    if (this.documentEventTokens['editModeMoveEnd'] !== undefined) {
      document.removeEventListener('mouseup', this.documentEventTokens['editModeMoveEnd']);
    }
  }

  _updateViewOffset() {
    const position = dojo.position(this.canvasElement, true);
    this.view.pageCanvasOffset = new Coordinate(position.x, position.y);
  }
}

export const VisualOptions = Object.freeze({
  AnimateBoards: Symbol('animateBoards'),
});
